import axios from 'axios';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { EServicesErrorCode } from 'core/model/enums/error-code.enum';
import { EProductOrigin } from 'core/model/enums/gtm.enum';
import { EProductType } from 'core/model/enums/product.enum';
import { ECartError } from 'core/model/enums/shopping-cart.enum';
import { IBusiness } from 'core/model/interfaces/business.interface';
import { IUser } from 'core/model/interfaces/customer.interface';
import { IServiceError } from 'core/model/interfaces/error.interface';
import { ICartProduct } from 'core/model/interfaces/product.interface';
import { IShoppingCartGroups, TShoppingCartPayload } from 'core/model/interfaces/shopping-cart.interface';
import { IGetShoppingCartParams } from 'core/services/shopping-cart/shopping-cart.dto';
import { getMinimumSaleMessage } from 'shared/utils/cart.utilts';
import { cloneDeepSimple } from 'shared/utils/object.utils';
import { getUnitMeasureByCode } from 'shared/utils/products.utils';
import { getAddressIdBySource } from '../checkout/checkout-state.utils';
import { EMPTY_SHOPPING_CART_AMOUNTS } from './constants';
import { ICartState, IChangeProductActionParams } from './types';

export const countTotalProductsInCart = (products: Array<{ quantity: number }>) => products.length;

export const getCartProductsByUserSource = (customerSource: ECustomerSource, itemsGroups: IShoppingCartGroups) => {
  switch (customerSource) {
    case ECustomerSource.APUDEX:
    case ECustomerSource.DEX:
      return cloneDeepSimple(itemsGroups.generalItems.products);
    case ECustomerSource.HYBRID:
      return [...itemsGroups.alicorpItems.products, ...itemsGroups.alliedItems.products];
    default:
      return [];
  }
};

export const hasCustomerReachedMinSaleAmounts = (customerSource: ECustomerSource, itemsGroups: IShoppingCartGroups) => {
  if (customerSource === ECustomerSource.APUDEX || customerSource === ECustomerSource.DEX) {
    return !getMinimumSaleMessage(itemsGroups.generalItems?.messages);
  }

  if (customerSource === ECustomerSource.HYBRID)
    return !(
      getMinimumSaleMessage(itemsGroups.alicorpItems?.messages) ||
      getMinimumSaleMessage(itemsGroups.alliedItems?.messages)
    );
};

export const buildOptimisticCart = (
  hasAlreadyAdded: boolean,
  products: Array<ICartProduct>,
  metaArgs: IChangeProductActionParams,
) => {
  const newProducts = cloneDeepSimple(products);
  const unitMeasure = getUnitMeasureByCode(metaArgs.product.unitMeasures, metaArgs.unitMeasureCode);

  if (hasAlreadyAdded) {
    const index = newProducts.findIndex(product => metaArgs.sku === product.sku);

    newProducts[index] = {
      ...products[index],
      ...(metaArgs.origin === EProductOrigin.DETAIL_PRODUCT && metaArgs.product),
      quantity: metaArgs.quantity,
      unitMeasure: {
        ...unitMeasure,
        promoIndicators: {
          hasDiscount: unitMeasure.promoIndicators.hasAnyDiscount,
        },
      },
      metadata: metaArgs.metadata,
    };
  } else {
    newProducts.push({
      ...metaArgs.product,
      quantity: metaArgs.quantity,
      unitMeasure: {
        ...unitMeasure,
        promoIndicators: {
          hasDiscount: unitMeasure.promoIndicators.hasAnyDiscount,
        },
      },
      amounts: EMPTY_SHOPPING_CART_AMOUNTS,
      promos: [],
      reducedQuantity: 0,
      metadata: metaArgs.metadata,
      stock: metaArgs.stock,
    });
  }
  return newProducts;
};

export const buildOptimisticCartByGroups = (
  customerSource: ECustomerSource,
  hasAlreadyAdded: boolean,
  state: ICartState,
  metaArgs: IChangeProductActionParams,
) => {
  const itemGroups = cloneDeepSimple(state.itemsGroups);
  switch (customerSource) {
    case ECustomerSource.APUDEX:
    case ECustomerSource.DEX:
      itemGroups.generalItems.products = buildOptimisticCart(
        hasAlreadyAdded,
        state.itemsGroups.generalItems?.products,
        metaArgs,
      );
      break;
    case ECustomerSource.HYBRID:
      if (metaArgs.product.sourceId === EProductType.ALICORP)
        itemGroups.alicorpItems.products = buildOptimisticCart(
          hasAlreadyAdded,
          state.itemsGroups.alicorpItems?.products,
          metaArgs,
        );
      if (metaArgs.product.sourceId === EProductType.ALLIED)
        itemGroups.alliedItems.products = buildOptimisticCart(
          hasAlreadyAdded,
          state.itemsGroups.alliedItems?.products,
          metaArgs,
        );
      break;
    default:
      break;
  }
  return itemGroups;
};

export const isProductAlreadyInCart = (
  customerSource: ECustomerSource,
  itemsGroups: IShoppingCartGroups,
  sku: string,
): boolean => {
  const products = getCartProductsByUserSource(customerSource, itemsGroups);
  return products.some(p => p.sku === sku);
};

export const handleErrorOnChangingProductQuantity = (error: unknown, dispatchCartAction: () => void) => {
  if (axios.isAxiosError<IServiceError>(error)) {
    if (error.response?.data?.error?.code === EServicesErrorCode.CART_STOCK_EXCEED) {
      dispatchCartAction();
    }
  }
  throw new Error(ECartError.UNEXPECTED_ERROR);
};

export const hasReducedProductsByCustomerSource = (
  customerSource: ECustomerSource,
  itemsGroups: IShoppingCartGroups,
) => {
  const products = getCartProductsByUserSource(customerSource, itemsGroups);
  return products?.some(product => product.reducedQuantity > 0);
};

export const getHasReducedProducts = (productsInCart: Array<ICartProduct>) =>
  productsInCart?.some(product => product.reducedQuantity > 0);

export const removeProductOptimistically = (sku: string, products: Array<ICartProduct>) => {
  const filteredProducts = cloneDeepSimple(products);
  return filteredProducts.filter(product => sku !== product.sku);
};

export const removeProductOptimisticallyByGroups = (
  customerSource: ECustomerSource,
  state: ICartState,
  sku: string,
  sourceId: number,
): IShoppingCartGroups => {
  const itemGroups = cloneDeepSimple(state.itemsGroups);
  switch (customerSource) {
    case ECustomerSource.APUDEX:
    case ECustomerSource.DEX:
      itemGroups.generalItems.products = removeProductOptimistically(sku, itemGroups.generalItems.products);
      break;
    case ECustomerSource.HYBRID:
      if (sourceId === EProductType.ALICORP)
        itemGroups.alicorpItems.products = removeProductOptimistically(sku, itemGroups.alicorpItems.products);
      if (sourceId === EProductType.ALLIED)
        itemGroups.alliedItems.products = removeProductOptimistically(sku, itemGroups.alliedItems.products);
      break;
    default:
      break;
  }
  return itemGroups;
};

export const getCustomerIdByProductSource = (customer: IUser, productSourceId: ECustomerSource | EProductType) => {
  const customerByProductSource = customer.customers.find(c => c.source === Number(productSourceId));
  return customerByProductSource?.id;
};

export const getShoppingCartParams = (
  payload: TShoppingCartPayload,
  address: IBusiness | Partial<IBusiness>,
): IGetShoppingCartParams => {
  const { alicorpAddressId, alliedAddressId } = getAddressIdBySource(address);

  const { cartAction } = payload ?? {};

  return {
    ...(cartAction && { cartAction }),
    ...(alicorpAddressId && { alicorpAddressId }),
    ...(alliedAddressId && { alliedAddressId }),
  };
};
